<template>
	<div class="layout" :key="initialized">
		<s-dialog
			v-model="showWatchDemoDlg"
			class="scrollTarget"
			:transition-duration="300"
			:no-backdrop-dismiss="false"
			:auto-close="false"
			@dialog-show="cmdShowWatchDemoDialogShow"
		>
			<YouTubeVideo video-id="Gwm_Odue2gc" @close="cmdCloseRegisterDlg"></YouTubeVideo>
		</s-dialog>
		<router-view />
	</div>
</template>

<script setup>
	import { ref, getCurrentInstance, onMounted, onBeforeMount, computed, inject, provide } from 'vue'
	import { useFlowControlsStore } from './stores/flowControls'
	import { ready } from '@/plugins/dom-events'
	import { stanAnalytics } from '@/plugins/stan-analytics'
	import { SDialog } from '@/components/SDialog'
	import YouTubeVideo from '@/components/YouTubeVideo'
	import { useLandingPage } from './stores/landingPage'
	import { event } from 'vue-gtag'
	import CONSTANTS from './helpers/constants'

	const landingPage = useLandingPage()
	const initialized = ref(false)
	provide('landingInitialized', initialized)
	const $configSettings = inject('configSettings')
	const {
		proxy: { $emitter },
	} = getCurrentInstance()
	const showWatchDemoDlg = ref(false)
	const fcStore = useFlowControlsStore()

	const registerUrl = computed(() => {
		return `${$configSettings.adminUrl}/register?signupFlow=default-modal`
	})

	if ($configSettings.environment === 'production') {
		/* eslint-disable */
		;(function (c, l, a, r, i, t, y) {
			c[a] =
				c[a] ||
				function () {
					;(c[a].q = c[a].q || []).push(arguments)
				}
			t = l.createElement(r)
			t.async = 1
			t.src = 'https://www.clarity.ms/tag/' + i
			y = l.getElementsByTagName(r)[0]
			y.parentNode.insertBefore(t, y)
		})(window, document, 'clarity', 'script', 'hkx2zp0ttt')

		// northbeam pixel
		;(function () {
			var t
			;((e = t = t || {}).A = 'identify'),
				(e.B = 'trackPageView'),
				(e.C = 'fireEmailCaptureEvent'),
				(e.D = 'fireCustomGoal'),
				(e.E = 'firePurchaseEvent'),
				(e.F = 'trackPageViewInitial')
			var e = '//j.northbeam.io/ota-sp/96d4c8b7-a73a-4df5-ae00-5606ed1e1beb.js'
			function r(e) {
				for (var n = [], t = 1; t < arguments.length; t++) n[t - 1] = arguments[t]
				a.push({ fnName: e, args: n })
			}
			var n,
				a = [],
				i =
					(((n = {})[t.F] = function (e) {
						r(t.F, e)
					}),
					((i = { _q: a })[t.A] = function (e, n) {
						return r(t.A, e, n)
					}),
					(i[t.B] = function () {
						return r(t.B)
					}),
					(i[t.C] = function (e, n) {
						return r(t.C, e, n)
					}),
					(i[t.D] = function (e, n) {
						return r(t.D, e, n)
					}),
					(i[t.E] = function (e) {
						return r(t.E, e)
					}),
					(window.Northbeam = i),
					document.createElement('script'))
			;(i.async = !0), (i.src = e), document.head.appendChild(i), n.trackPageViewInitial(window.location.href)
		})()
	}

	ready(async () => {
		fcStore.$patch({ fcpComplete: true })
	})
	provide('$emitter', $emitter)
	function buildUrl(baseUrl, params) {
		// Filter out falsy values from the params object
		const filteredParams = Object.entries(params)
			.filter(([key, value]) => Boolean(value)) // Keep only truthy values
			.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`) // Encode the params

		// Construct the query string
		const queryString = filteredParams.join('&')

		// Return the final URL
		return queryString ? `${baseUrl}?${queryString}` : baseUrl
	}

	const registerEvent = section => {
		const urlString = window.location.href
		const url = new URL(urlString)
		const referralUser = url.searchParams.get('ref')
		const trialDays = landingPage.trialDays
		const campaignParams = {
			ref: referralUser,
			code: url.searchParams.get('code'),
			utm_source: url.searchParams.get('utm_source'),
			utm_medium: url.searchParams.get('utm_medium'),
			utm_campaign: url.searchParams.get('utm_campaign'),
			utm_term: url.searchParams.get('utm_term'),
			subscription_tier: landingPage.pageContent?.subscriptionTier,
			trial_code: landingPage.pageContent?.trialCode,
		}
		trialDays === '30' && (campaignParams.trialDays = trialDays)
		const isSubscriptionCampaign = landingPage.pageContent?.subscriptionTier && landingPage.pageContent?.trialCode
		const baseNewUrl = isSubscriptionCampaign ? `${$configSettings.adminUrl}/subscribe/plan` : `${$configSettings.adminUrl}/register`
		const props = {
			section,
			ref: referralUser,
		}
		stanAnalytics('landing-registration-trigger', { meta: { username: 'guest' }, props })

		// Details: https://www.notion.so/stanforcreators/Google-Ads-Tracking-19a37ad349e4800b9004cd4705ce8062
		event('conversion', { send_to: CONSTANTS.GOOGLE_AD_CONV_ACTION.STAN.SIGN_UP })
		event('conversion', { send_to: CONSTANTS.GOOGLE_AD_CONV_ACTION.STAN_V2.SIGN_UP })
		const newUrl = buildUrl(baseNewUrl, campaignParams)
		window.location.href = newUrl
		return false
	}
	const signIn = section => {
		const urlString = window.location.href
		const url = new URL(urlString)
		const baseNewUrl = `${$configSettings.adminUrl}/`
		const referralUser = url.searchParams.get('ref')
		const trialDays = landingPage.trialDays
		const params = {
			ref: referralUser,
			utm_source: url.searchParams.get('utm_source'),
			utm_medium: url.searchParams.get('utm_medium'),
			utm_campaign: url.searchParams.get('utm_campaign'),
			utm_term: url.searchParams.get('utm_term'),
			subscription_tier: landingPage.pageContent?.subscriptionTier,
			trial_code: landingPage.pageContent?.trialCode,
		}
		trialDays === '30' && (params.trialDays = trialDays)
		const props = {
			section,
			ref: referralUser,
		}
		stanAnalytics('landing-signin-trigger', { meta: { username: 'guest' }, props })

		const newUrl = buildUrl(baseNewUrl, params)
		window.location.href = newUrl
		return false
	}

	const registerByUrl = msg => {
		register(msg)
	}

	function cmdCloseRegisterDlg() {
		showWatchDemoDlg.value = false
	}

	const stanAnalyticsCall = async (name, section) => {
		const props = {
			section: section,
		}
		stanAnalytics(name, { meta: { username: 'guest' }, props })
	}

	function cmdWatchDemoVideo(section) {
		showWatchDemoDlg.value = true
		stanAnalyticsCall('landing-play-demo', section)
	}

	function cmdHomeVideoPlaying(section) {
		stanAnalyticsCall('landing-video-playing', '')
	}

	function cmdCloseWatchDemoVideo() {
		showWatchDemoDlg.value = false
	}

	const cmdShowWatchDemoDialogShow = async msg => {}

	const innerWidth = ref(0)
	const innerHeight = ref(0)

	function windowResize() {
		innerWidth.value = document.body.clientWidth
		innerHeight.value = document.body.clientHeight
	}

	// Added Status page
	const statusPage = document.createElement('script')
	statusPage.setAttribute('src', 'https://status.stan.store/embed/script.js')
	document.head.appendChild(statusPage)

	onMounted(async () => {
		await landingPage.initialize()
		initialized.value = true
		windowResize()
		$emitter.on('register', registerEvent)
		$emitter.on('sign.in', signIn)
		$emitter.on('home.video.playing', cmdHomeVideoPlaying)
		$emitter.on('watch.demo.video', cmdWatchDemoVideo)
		window.addEventListener('resize', () => windowResize())
		window.addEventListener(
			'message',
			event => {
				if (event.origin === $configSettings.adminUrl && event.data.user_registered) {
					window.location.href = $configSettings.adminUrl
				}
			},
			false
		)
	})
	onBeforeMount(() => {
		$emitter.off('register', registerEvent)
		$emitter.off('sign.in', signIn)
		$emitter.off('watch.demo.video', cmdWatchDemoVideo)
		window.removeEventListener('resize', () => windowResize())
	})
</script>
<style lang="postcss">
	@import '@/assets/styles/bootstrap.min.css';
	@import '@/assets/styles/main.css';

	.layout {
		font-family: 'Grobek', sans-serif;
		background-size: cover;
	}
</style>
